import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ConfirmationModalProps, Error } from '~/Components';
import { IStaff } from '~/Models/DTO/Staff.model';
import { Dots, FormControl, InputLabel, MenuItem, Select, Typography } from '~/UIKit';
import { makeStyles } from '~/UIKit/Style';
import { useStaffDeactivationDeletion } from './useStaffDeactivationDeletion';

const useStyles = makeStyles(
  {
    select: {
      margin: '15px 15px 0px 15px',
      width: '-webkit-fill-available',
    },
  },
  { name: 'StaffDeactivationModal' },
);

export const StaffDeactivationDeletion = ({
  staff,
  isInDeletionMode = false,
  setConfirmationModalProps,
}: {
  staff: IStaff;
  isInDeletionMode?: boolean;
  setConfirmationModalProps: React.Dispatch<React.SetStateAction<ConfirmationModalProps>>;
}) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const {
    selectedTrainerId,
    setSelectedTrainerId,
    trainerList,
    hasEvents,
    areEventsLoading,
    isTrainerListLoading,
    events,
    error,
  } = useStaffDeactivationDeletion({ staff, isInDeletionMode, setConfirmationModalProps });

  if (areEventsLoading) {
    return <Dots />;
  }

  // show the first three names of items in data, if more show '...'
  const names = events?.map((item) => item.name);
  const moreNames = names?.length && names?.length > 3 ? '...' : '';
  const firstThreeNames = names?.slice(0, 3).join(', ') + moreNames;

  const onTrainerSelected = (trainerId: string) => () => {
    setSelectedTrainerId(trainerId);
  };

  return (
    <div>
      <Error error={error} />
      <Typography>
        <Trans
          i18nKey={isInDeletionMode ? 'staff.deleteTrainer' : 'staff.deactivateTrainer'}
          values={{ name: `${staff.firstName} ${staff.lastName}` }}
          components={[<b key="bold" />]}
        />
      </Typography>
      {hasEvents && (
        <>
          <Typography>{t('staff.chooseReplacement', { events: firstThreeNames })}</Typography>
          {isTrainerListLoading ? (
            <Dots />
          ) : (
            <FormControl fullWidth className={classes.select}>
              <InputLabel id="staff-member">
                <Typography>{t('staff.chooseStaffMember')}</Typography>
              </InputLabel>
              <Select
                value={selectedTrainerId}
                labelId="staff-member"
                label={t('staff.chooseStaffMember')}
              >
                {trainerList
                  ?.filter((trainer) => trainer.trainerId !== staff.id)
                  .map((trainer) => (
                    <MenuItem
                      key={trainer.trainerId}
                      value={trainer.trainerId}
                      onClick={onTrainerSelected(trainer.trainerId)}
                    >
                      <Typography>
                        {trainer.firstName} {trainer.lastName}
                      </Typography>
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          )}
        </>
      )}
    </div>
  );
};
